import { createSlice, createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
    fetchTickerData, fetchFilteredTickers, fetchETFs, fetchXrayData, fetchUpcomingEvents,
    fetchQuotes, listHedgeFunds, fetchHedgeFund, fetchStockFinancials
} from "../../api/tickersApi";

const initialTickersState = {
    tickers: {}, // Stores ticker data
    filteredTickers: [],
    allFilteredTickers: [],
    hedgeFunds: [],
    badTickers: [], // Array to store symbols of tickers that don't return data
    etfs: {
      positions: {},
      status: 'idle',
      error: null,
      anchor: null,
    },
    xrayData: {}, // Object to store results from the xray API
    dividendSchedule: {
        items: [],
        status: 'idle'
    },
    quotes: {},
    userSelected: {
        screensSelectedList: [],
        stockSelectedList: [],
        etfSelectedList: [],
        // Can add more selected lists here in the future
    },
    status: 'idle',
    error: null,

}

const tickersSlice = createSlice({
  name: 'tickers',
  initialState: initialTickersState,
  reducers: {
      addToSelectedList: (state, action) => {
          const { listField, symbols } = action.payload;
          const symbolsArray = Array.isArray(symbols) ? symbols : [symbols];

          if (!state.userSelected[listField]) {
            state.userSelected[listField] = [];
          }

          state.userSelected[listField] = [...new Set([
            ...state.userSelected[listField],
            ...symbolsArray
          ])];
        },

        removeFromSelectedList: (state, action) => {
          const { listField, symbols } = action.payload;
          const symbolsArray = Array.isArray(symbols) ? symbols : [symbols];

          if (state.userSelected[listField]) {
            state.userSelected[listField] = state.userSelected[listField]
              .filter(symbol => !symbolsArray.includes(symbol));
          }
        },

    clearSelectedList: (state, action) => {
      const { listField } = action.payload;
      if (state.userSelected[listField]) {
        state.userSelected[listField] = [];
      }
    }
  },
  extraReducers(builder) {
    builder

      .addCase(fetchTickerData.fulfilled, (state, action) => {
        if (action.payload.fetchedTickers.length > 0 || action.payload.badTickers.length > 0) {
            action.payload.fetchedTickers.forEach(ticker => {
                const existingData = state.tickers[ticker.symbol] || {};
                state.tickers[ticker.symbol] = _.merge({}, existingData, ticker);
            });

            // Update badTickers without duplicating entries
            state.badTickers = [...new Set([...state.badTickers, ...action.payload.badTickers])];
            state.status = 'succeeded';
        }
      })
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        state.quotes = { ...state.quotes, ...action.payload };
        state.status = 'succeeded';
      })
      .addCase(fetchQuotes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchXrayData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Assume action.payload contains the structure needed for xray data
        state.xrayData = action.payload;
      })
      .addCase(fetchFilteredTickers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.filteredTickers = action.payload;
        state.allFilteredTickers = [...state.allFilteredTickers, ...action.payload];
      })
      .addCase(fetchETFs.fulfilled, (state, action) => {
        state.etfs.status = 'succeeded';
        action.payload.forEach(etf => {
          state.etfs.positions[etf.symbol] = { ...etf, limit: 0, weight: 0 };
        });
      })
        .addCase(fetchUpcomingEvents.fulfilled, (state, action) => {

        })
      .addCase(fetchFilteredTickers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchTickerData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchETFs.rejected, (state, action) => {
        state.etfs.status = 'failed';
        state.etfs.error = action.error.message;
      })
      .addCase(fetchXrayData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchETFs.pending, (state) => {state.etfs.status = 'loading'})
      .addCase(fetchFilteredTickers.pending, (state) => {state.status = 'loading'})
      .addCase(fetchTickerData.pending, (state) => {state.status = 'loading'})
      .addCase(fetchXrayData.pending, (state) => {state.status = 'loading'})
      .addCase(listHedgeFunds.fulfilled, (state, action) => {
          // Avoid duplicates by merging unique entries
          const uniqueHedgeFunds = action.payload
          state.hedgeFunds = uniqueHedgeFunds;
          state.status = 'succeeded';
        })
      .addCase(listHedgeFunds.rejected, (state, action) => {
          state.status = 'failed';
          state.error = action.payload;
        })
      .addCase(listHedgeFunds.pending, (state) => {
          state.status = 'loading';
      })
        .addCase(fetchHedgeFund.fulfilled, (state, action) => {
      const { name, data } = action.payload;
      const existingFund = state.hedgeFunds.find((fund) => fund.name === name);

      if (existingFund) {
        // Merge new data with the existing object
        _.merge(existingFund, data);
      } else {
        // Add new hedge fund data if it doesn't already exist
        state.hedgeFunds.push({ name, ...data });
      }

      state.status = 'succeeded';
    })
    .addCase(fetchHedgeFund.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    })
    .addCase(fetchHedgeFund.pending, (state) => {
          state.status = 'loading';
    })
    .addCase(fetchStockFinancials.pending, (state) => {
      state.status = 'loading';
    })
    .addCase(fetchStockFinancials.fulfilled, (state, action) => {
      state.status = 'succeeded';
      const { financials, symbol } = action.payload;
      // If we have new financials to add (array of financial data)
      if (financials.length > 0) {
        const existingTicker = state.tickers[symbol] || {};
        const existingFinancials = existingTicker.financials || [];

        // Merge and dedupe by timestamp
        state.tickers[symbol] = {
          ...existingTicker,
          financials: _.uniqBy(
            [...financials, ...existingFinancials],
            'timestamp'
          )
        };
      }
    })
    .addCase(fetchStockFinancials.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    });
  }
});



/**
 * Selector to get multiple ticker attributes at once.
 * @param {Object} state - The global state.
 * @param {Array} symbols - An array of ticker symbols.
 * @param {String} path - The dot path to the attribute in each ticker's data.
 * @returns {Object} An object with symbols as keys and the specified attribute as values.
 */
export const selectAllTickerAttrs = createSelector(
  [state => state.tickers, (state, symbols, paths) => ({ symbols, paths })],
  (tickers, { symbols, paths }) => {
    // If the tickers state is not available or empty, return an empty object.
    if (!tickers || !tickers.tickers) {
      return {};
    }

    return symbols.reduce((acc, symbol) => {
      // If the ticker data has not been fetched yet, skip this symbol.
      const tickerData = tickers.tickers[symbol];
      if (!tickerData || tickerData.status === 'loading' || tickerData.status === 'not_fetched') {
        acc[symbol] = {}; // Return an empty object if data is not available
        return acc;
      }

      const attributesForSymbol = paths.reduce((attrAcc, path) => {
        let attrValue = _.get(tickerData, path);

        // Check if the value is an object that might have been misinterpreted as an array
        if (typeof attrValue === 'object' && attrValue !== null && !Array.isArray(attrValue)) {
          // Convert numeric keys to numbers for objects, but leave arrays as they are
          const convertedObject = Object.entries(attrValue).reduce((obj, [key, value]) => {
            const numericKey = Number(key);
            obj[isNaN(numericKey) ? key : numericKey] = value;
            return obj;
          }, {});
          attrValue = convertedObject;
        }

        if (attrValue !== undefined) { // Check explicitly for undefined to allow null values
          _.set(attrAcc, path, attrValue); // Use _.set to handle dot paths correctly
        }
        return attrAcc;
      }, {});

      acc[symbol] = attributesForSymbol;
      return acc;
    }, {});
  }
);

export const {
  addToSelectedList,
  removeFromSelectedList,
  clearSelectedList
} = tickersSlice.actions;


export const selectSelectedList = (state, listField) =>
  state.tickers.userSelected[listField] || [];

export default tickersSlice.reducer;